import { useQuery } from "@apollo/client"
import { ReactNode, useState } from "react"
import { useForm } from "react-hook-form"
import { gql } from "~/__generated__"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/errors"
import { Form, FormField } from "~/ui/form"
import { InfiniteLoadMore } from "~/ui/infinite-load-more"
import { SearchInput } from "~/ui/search-input"
import Text from "~/ui/typography"
import { adminInvitationColumns } from "./admin-invitation-columns"
import { DataTable } from "./data-table"

export const adminInvitationsQuery = gql(/* GraphQL */ `
  query AdminInvitations($first: Int = 20, $after: String, $filters: InvitationFiltersInput) {
    invitations(first: $first, after: $after, filters: $filters) {
      edges {
        node {
          id
          title
          token
          status
          contactsAddedCount
          createdAt
          inviteUrl
          user {
            id
            fullName
            handle
          }
          tags {
            id
            name
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`)

type FormData = {
  search: string
}

interface AdminInvitationsTableProps {
  title: string
  description: string
  placeholder?: string
  actionButton?: ReactNode
}

export function AdminInvitationsTable({
  title,
  description,
  placeholder = "Search by title or URL",
  actionButton,
}: AdminInvitationsTableProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const form = useForm<FormData>({
    defaultValues: {
      search: "",
    },
  })
  const { data, fetchMore, error, loading } = useQuery(adminInvitationsQuery, {
    variables: {
      first: 20,
      after: null,
      filters: {
        search: searchTerm || undefined,
      },
    },
    notifyOnNetworkStatusChange: true,
  })

  if (error) {
    return <GraphqlError error={error} />
  }

  const invitations = data?.invitations.edges.map((edge) => edge.node) ?? []
  const pageInfo = data?.invitations.pageInfo

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
  }

  const clearSearchAndFilters = () => {
    form.reset()
    setSearchTerm("")
  }

  const handleLoadMore = () => {
    if (pageInfo?.hasNextPage) {
      fetchMore({
        variables: {
          after: pageInfo.endCursor,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previousResult
          return {
            invitations: {
              ...fetchMoreResult.invitations,
              edges: [...previousResult.invitations.edges, ...fetchMoreResult.invitations.edges],
            },
          }
        },
      })
    }
  }

  return (
    <TablePageLayout>
      <div className="mb-6 flex items-center justify-between">
        <Form {...form}>
          <form className="flex w-full flex-col">
            <div className="flex w-full items-center justify-between">
              <div>
                <Text as="h1" variant="heading3">
                  {title}
                </Text>
                <p className="mt-1 text-sm text-gray-500">{description}</p>
              </div>

              <div className="flex items-center gap-2">
                <FormField
                  control={form.control}
                  name="search"
                  render={({ field }) => (
                    <SearchInput
                      placeholder={placeholder}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e)
                        handleSearchChange(e.target.value)
                      }}
                    />
                  )}
                />
                {actionButton}
              </div>
            </div>
          </form>
        </Form>
      </div>

      <div className="mb-6 h-px bg-gray-200"></div>

      {invitations.length > 0 && (
        <>
          <DataTable columns={adminInvitationColumns} data={invitations} enableRowClick={false} />
          <InfiniteLoadMore
            onEndReached={handleLoadMore}
            canLoadMore={pageInfo?.hasNextPage ?? false}
            loadingText="Loading more invitations..."
            loading={loading}
          />
        </>
      )}

      {!loading && !error && invitations.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <h3 className="text-lg font-medium text-gray-900">No Invitations Found</h3>
          <p className="mt-2 text-sm text-gray-500">
            {searchTerm
              ? "Clear your search results to view all invitations"
              : "No invitation links have been created yet."}
          </p>
          {searchTerm && (
            <Button variant="outline" onClick={clearSearchAndFilters} className="mt-4">
              Clear Search
            </Button>
          )}
        </div>
      )}
    </TablePageLayout>
  )
}
